// Admin-only Manager intake-duty panel (#1261): lists eligible managers
// (active + system `manager` role) with an on/off toggle each, plus an
// empty-duty warning when no manager is on duty (intake leads would have
// nobody to route to).
import { useTranslation } from 'react-i18next';
import type { ManagerDutyItem } from '@magimp/shared';
import { useManagerDutyList, useToggleManagerDuty } from '@magimp/shared-hooks';
import { EmptyState, ErrorState, LoadingState } from '../../ui/index.js';
import { errorMessage } from '../../api/errors.js';
import { useToastStore } from '../../stores/toast.js';

export function ManagerDutyPanel() {
  const { t } = useTranslation(['callCenterAdmin', 'common']);
  const dutyQ = useManagerDutyList();

  return (
    <section data-testid="manager-duty-panel">
      <h2 style={{ fontSize: 16, fontWeight: 600 }}>{t('callCenterAdmin:duty.title')}</h2>

      {dutyQ.isPending && <LoadingState text={t('common:states.loading')} />}
      {dutyQ.isError && (
        <ErrorState
          title={t('common:states.errorTitle')}
          text={errorMessage(dutyQ.error)}
          onRetry={() => dutyQ.refetch()}
        />
      )}
      {dutyQ.data && dutyQ.data.items.length === 0 && (
        <EmptyState title={t('callCenterAdmin:duty.empty')} />
      )}
      {dutyQ.data && dutyQ.data.items.length > 0 && (
        <>
          {!dutyQ.data.anyOnDuty && (
            <div
              data-testid="empty-duty-warning"
              style={{
                margin: '8px 0',
                padding: '8px 12px',
                borderRadius: 'var(--r-1)',
                background: 'var(--status-warn-bg)',
                color: 'var(--status-warn-fg)',
                fontSize: 13,
              }}
            >
              {t('callCenterAdmin:duty.emptyDutyWarning')}
            </div>
          )}
          <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0' }}>
            {dutyQ.data.items.map((item) => (
              <DutyRow key={item.userId} item={item} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function DutyRow({ item }: { item: ManagerDutyItem }) {
  const { t } = useTranslation(['callCenterAdmin']);
  const toggle = useToggleManagerDuty(item.userId);
  const show = useToastStore((s) => s.show);

  const onToggle = () => {
    if (toggle.isPending) return;
    toggle.mutate(
      { onDuty: !item.onDuty },
      { onError: (err) => show({ tone: 'error', text: errorMessage(err) }) },
    );
  };

  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderTop: '1px solid var(--border-1)',
        fontSize: 13,
      }}
    >
      <span>
        {item.name} <span style={{ color: 'var(--fg-2)' }}>{item.email}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={item.onDuty}
        aria-label={
          item.onDuty ? t('callCenterAdmin:duty.ariaOn') : t('callCenterAdmin:duty.ariaOff')
        }
        data-testid={`duty-toggle-${item.userId}`}
        disabled={toggle.isPending}
        onClick={onToggle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: 28,
          padding: '0 10px',
          borderRadius: 'var(--r-1)',
          border: '1px solid var(--border-1)',
          background: 'var(--bg-1)',
          color: 'var(--fg-3)',
          fontSize: 12,
          cursor: toggle.isPending ? 'progress' : 'pointer',
          opacity: toggle.isPending ? 0.6 : 1,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 24,
            height: 14,
            borderRadius: 999,
            background: item.onDuty ? 'var(--accent-1)' : 'var(--border-2)',
            position: 'relative',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 1,
              left: item.onDuty ? 11 : 1,
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: 'var(--bg-1)',
            }}
          />
        </span>
        <span>{item.onDuty ? t('callCenterAdmin:duty.on') : t('callCenterAdmin:duty.off')}</span>
      </button>
    </li>
  );
}
